import { PointerEvent, useRef } from 'react';
import { X } from 'lucide-react';
import type { DocumentSignFieldInput } from '@/lib/api';
import { getRecipientColor } from './recipientColors';

export interface DraftField extends DocumentSignFieldInput {
  /** Stable React key. Persisted fields use server `fieldId`; new fields
   *  get a synthetic id so resaves keep their identity in the list. */
  key: string;
}

interface FieldOverlayProps {
  field: DraftField;
  /** Index of the recipient owning this field — drives the overlay color. */
  recipientIndex: number;
  /** Recipient name for screen readers and the legend. */
  recipientName: string;
  pageWidth: number;
  pageHeight: number;
  selected: boolean;
  readOnly?: boolean;
  onSelect: () => void;
  onChange: (next: Pick<DraftField, 'x' | 'y' | 'w' | 'h'>) => void;
  onRemove: () => void;
}

const FIELD_LABEL: Record<DraftField['type'], string> = {
  signature: 'Signature',
  initial: 'Initial',
  date_signed: 'Date',
  signer_name: 'Name',
  email: 'Email',
  company: 'Company',
  title: 'Title',
  text: 'Text',
  number: 'Number',
  checkbox: 'Check',
};

const MIN_PIXEL_SIZE = 18;

/**
 * Absolutely-positioned overlay for one signature field. Coordinates are
 * stored as page-fractions in [0,1]; this component multiplies by the
 * rendered page size for screen positioning, then clamps back to [0,1] on
 * commit so values written to the server stay in spec regardless of how the
 * pointer wandered.
 */
export function FieldOverlay({
  field,
  recipientIndex,
  recipientName,
  pageWidth,
  pageHeight,
  selected,
  readOnly = false,
  onSelect,
  onChange,
  onRemove,
}: FieldOverlayProps) {
  const dragStateRef = useRef<{
    mode: 'move' | 'resize';
    startPointerX: number;
    startPointerY: number;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
  } | null>(null);

  const color = getRecipientColor(recipientIndex);

  const left = field.x * pageWidth;
  const top = field.y * pageHeight;
  const width = field.w * pageWidth;
  const height = field.h * pageHeight;

  function clamp(n: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, n));
  }

  function handlePointerDown(e: PointerEvent<HTMLDivElement>, mode: 'move' | 'resize') {
    if (readOnly) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect();

    dragStateRef.current = {
      mode,
      startPointerX: e.clientX,
      startPointerY: e.clientY,
      startX: field.x,
      startY: field.y,
      startW: field.w,
      startH: field.h,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e: PointerEvent<HTMLDivElement>) {
    const drag = dragStateRef.current;
    if (!drag) return;
    e.stopPropagation();

    const dxFrac = (e.clientX - drag.startPointerX) / pageWidth;
    const dyFrac = (e.clientY - drag.startPointerY) / pageHeight;

    if (drag.mode === 'move') {
      const x = clamp(drag.startX + dxFrac, 0, 1 - drag.startW);
      const y = clamp(drag.startY + dyFrac, 0, 1 - drag.startH);
      onChange({ x, y, w: drag.startW, h: drag.startH });
    } else {
      const minWFrac = MIN_PIXEL_SIZE / pageWidth;
      const minHFrac = MIN_PIXEL_SIZE / pageHeight;
      const w = clamp(drag.startW + dxFrac, minWFrac, 1 - drag.startX);
      const h = clamp(drag.startH + dyFrac, minHFrac, 1 - drag.startY);
      onChange({ x: drag.startX, y: drag.startY, w, h });
    }
  }

  function handlePointerUp(e: PointerEvent<HTMLDivElement>) {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // capture may have already been released
    }
  }

  return (
    <div
      role="button"
      tabIndex={readOnly ? -1 : 0}
      aria-label={`${FIELD_LABEL[field.type]} for ${recipientName}`}
      className="absolute box-border select-none"
      style={{
        left,
        top,
        width,
        height,
        background: color.background,
        border: `2px solid ${color.border}`,
        cursor: readOnly ? 'default' : 'move',
        outline: selected ? `2px solid ${color.border}` : 'none',
        outlineOffset: '2px',
      }}
      onPointerDown={(e) => handlePointerDown(e, 'move')}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      // A pointer-drag to move/resize ends with a synthetic click; swallow it
      // (and any plain click on a field) so it can't bubble to the page
      // overlay and place a brand-new field where this one already sits.
      onClick={(e) => e.stopPropagation()}
    >
      <span
        className="absolute top-0 left-0 px-1 text-[10px] font-medium text-white"
        style={{ background: color.border }}
      >
        {FIELD_LABEL[field.type]}
      </span>
      {!readOnly && selected && (
        <>
          <button
            type="button"
            aria-label="Remove field"
            className="absolute -top-2 -right-2 rounded-full bg-white text-destructive shadow border"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            style={{ width: 18, height: 18, lineHeight: 0 }}
          >
            <X className="h-3 w-3 mx-auto" aria-hidden="true" />
          </button>
          <div
            role="presentation"
            className="absolute bottom-0 right-0"
            style={{
              width: 12,
              height: 12,
              background: color.border,
              cursor: 'nwse-resize',
            }}
            onPointerDown={(e) => handlePointerDown(e, 'resize')}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          />
        </>
      )}
    </div>
  );
}
